export type MagneticHandleSide = 'left' | 'right';

export type MagneticHandlePoint = {
  x: number;
  y: number;
};

export type MagneticHandleConfig = {
  initialOffset: number;
  minGap: number;
  magnetRadius: number;
  hiddenShift: number;
  magnetizedScale: number;
};

export const DEFAULT_MAGNETIC_HANDLE_CONFIG: MagneticHandleConfig = {
  initialOffset: 24,
  minGap: 20,
  magnetRadius: 48,
  hiddenShift: 32,
  magnetizedScale: 1.08,
};

export function resolveMagneticHandleConfig(config?: Partial<MagneticHandleConfig>): MagneticHandleConfig {
  return {
    ...DEFAULT_MAGNETIC_HANDLE_CONFIG,
    ...config,
  };
}

export function resolveDefaultMagneticHandlePoint(
  nodeWidth: number,
  nodeHeight: number,
  side: MagneticHandleSide,
  config: MagneticHandleConfig = DEFAULT_MAGNETIC_HANDLE_CONFIG,
): MagneticHandlePoint {
  const offset = config.initialOffset;

  return {
    x: side === 'left' ? -offset : nodeWidth + offset,
    y: nodeHeight / 2,
  };
}

function resolveScreenPoint(
  rect: DOMRect,
  nodeWidth: number,
  nodeHeight: number,
  side: MagneticHandleSide,
  zoom: number,
  config: MagneticHandleConfig,
) {
  const localPoint = resolveDefaultMagneticHandlePoint(nodeWidth, nodeHeight, side, config);

  return {
    x: rect.left + localPoint.x * zoom,
    y: rect.top + localPoint.y * zoom,
  };
}

export function resolveMagnetizedHandlePoint({
  allowedSide,
  clientX,
  clientY,
  config = DEFAULT_MAGNETIC_HANDLE_CONFIG,
  nodeHeight,
  nodeWidth,
  rect,
  zoom,
}: {
  allowedSide: MagneticHandleSide;
  clientX: number;
  clientY: number;
  config?: MagneticHandleConfig;
  nodeHeight: number;
  nodeWidth: number;
  rect: DOMRect;
  zoom: number;
}) {
  const safeZoom = zoom > 0 ? zoom : 1;
  const localX = (clientX - rect.left) / safeZoom;
  const localY = (clientY - rect.top) / safeZoom;
  const defaultPoint = resolveDefaultMagneticHandlePoint(nodeWidth, nodeHeight, allowedSide, config);
  const defaultScreenPoint = resolveScreenPoint(rect, nodeWidth, nodeHeight, allowedSide, safeZoom, config);
  const distance = Math.hypot(clientX - defaultScreenPoint.x, clientY - defaultScreenPoint.y);

  if (distance > config.magnetRadius * safeZoom) {
    return {
      point: defaultPoint,
      side: null,
    } as const;
  }

  const maxOffset = config.initialOffset + config.magnetRadius;
  const maxY = nodeHeight > 0 ? nodeHeight : 0;
  const nextPoint = {
    x:
      allowedSide === 'left'
        ? (localX < -maxOffset ? -maxOffset : localX > -config.minGap ? -config.minGap : localX)
        : (localX < nodeWidth + config.minGap ? nodeWidth + config.minGap : localX > nodeWidth + maxOffset ? nodeWidth + maxOffset : localX),
    y: localY < 0 ? 0 : localY > maxY ? maxY : localY,
  };

  return {
    point: nextPoint,
    side: allowedSide,
  } as const;
}

export function resolveNearestMagnetizedHandlePoint({
  clientX,
  clientY,
  config = DEFAULT_MAGNETIC_HANDLE_CONFIG,
  nodeHeight,
  nodeWidth,
  rect,
  zoom,
}: {
  clientX: number;
  clientY: number;
  config?: MagneticHandleConfig;
  nodeHeight: number;
  nodeWidth: number;
  rect: DOMRect;
  zoom: number;
}) {
  const leftCandidate = resolveMagnetizedHandlePoint({
    allowedSide: 'left',
    clientX,
    clientY,
    config,
    nodeHeight,
    nodeWidth,
    rect,
    zoom,
  });
  const rightCandidate = resolveMagnetizedHandlePoint({
    allowedSide: 'right',
    clientX,
    clientY,
    config,
    nodeHeight,
    nodeWidth,
    rect,
    zoom,
  });
  const safeZoom = zoom > 0 ? zoom : 1;
  const leftDistance = Math.hypot(clientX - rect.left - leftCandidate.point.x * safeZoom, clientY - rect.top - leftCandidate.point.y * safeZoom);
  const rightDistance = Math.hypot(clientX - rect.left - rightCandidate.point.x * safeZoom, clientY - rect.top - rightCandidate.point.y * safeZoom);

  if (leftCandidate.side === null && rightCandidate.side === null) {
    return {
      point: leftDistance <= rightDistance ? leftCandidate.point : rightCandidate.point,
      side: null,
    } as const;
  }

  if (leftCandidate.side !== null && rightCandidate.side === null) {
    return leftCandidate;
  }

  if (leftCandidate.side === null && rightCandidate.side !== null) {
    return rightCandidate;
  }

  return leftDistance <= rightDistance ? leftCandidate : rightCandidate;
}
